import React, { FC, useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { FormControl, Grid, InputLabel, TextField, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { withStyles } from '@mui/styles'

import { CrmUserRequestResetPasswordRequestDto } from '@sparling/financial-common'
import useStyles from './useStyle'
import { rxios } from '../../utils/NetUtils'
import { routes } from '../../constants/routes'
import { GreenButton } from '../../components/Buttons/GreenButton'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { OutlinedButton } from '../../components/Buttons/OutlinedButton'

const resolver = classValidatorResolver(CrmUserRequestResetPasswordRequestDto)

type Inputs = {
  email: string
}

const CustomFormControl = withStyles((theme: Theme) => ({
  root: {
    '& label': {
      color: theme.palette.primary.light,
      opacity: '60%',
    },
    '& input:valid': {
      fontSize: 16,
      border: '1px solid rgba(255,255,255,0.6)',
      borderRadius: 4,
      padding: 10,
    },
    '& input:valid:hover + fieldset': {
      fontSize: 16,
      border: `1px solid ${theme.palette.primary.light}`,
    },
    '& input:valid:active + fieldset': {
      fontSize: 16,
      border: `1px solid ${theme.palette.secondary.main}`,
    },
    '& input:valid + fieldset': {
      fontSize: 16,
      opacity: 0.6,
      color: theme.palette.primary.light,
    },
    '& fieldset': {
      color: 'red',
    },
  },
}))(FormControl)

export const RequestResetPassword: FC = () => {
  const classes = useStyles()
  const formMethods = useForm<Inputs>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver,
  })

  const {
    handleSubmit,
    register,
    formState: { isValid, errors },
  } = formMethods

  const history = useHistory()
  const [loading, setLoading] = useState<boolean>(false)

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data: Inputs) => {
      let cleanupFunction = false
      setLoading(true)

      rxios.post('password/reset', data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          if (next.success) {
            toast.success('Password was reseted')
            if (!cleanupFunction) setLoading(false)
            history.push(routes.LOGIN)
          } else {
            toast.error('Can not reset password')
            setLoading(false)
          }
        },
        // eslint-disable-next-line
        (error: any) => {
          toast.error(error.response.data.message)
          setLoading(false)
        },
        () => {
          // eslint-disable-next-line
          if (!cleanupFunction) setLoading(false)
        },
      )
      cleanupFunction = true
    },
    [history],
  )

  const errorMessage = useCallback(
    (inputName: string) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const error = errors[inputName]
      const message = error?.message.replace(inputName, '')
      return capitalizeFirstLetter(message)
    },
    [errors],
  )

  return (
    <FormProvider {...formMethods}>
      <main className={classes.main}>
        <Grid container className={classes.container}>
          <Typography variant={'h2'}>Reset Password</Typography>

          <form className={classes.form} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Grid container className={classes.formWrap}>
              <Grid item xs={12} className={classes.inputWrap}>
                <CustomFormControl className={classes.formControl}>
                  <InputLabel shrink htmlFor="email" className={classes.label}>
                    Email
                  </InputLabel>
                  <TextField className={classes.input} id="email" variant="outlined" fullWidth {...register('email')} />
                </CustomFormControl>
                {errors.email && (
                  <Typography variant="body1" gutterBottom className={classes.errorField}>
                    {errorMessage('email')}
                  </Typography>
                )}
              </Grid>

              <GreenButton type="submit" width="100%" mt={42} disabled={!isValid || loading} text="Reset Password" />
              <OutlinedButton width="100%" mt={18} mb={30} href={routes.LOGIN} text="Back to Login" />
            </Grid>
          </form>
        </Grid>
      </main>
    </FormProvider>
  )
}
